/**
 * Quản trị nhà cung cấp thiết bị và bộ tương thích — `/quan-tri/nha-cung-cap`.
 *
 * Hai bảng: nhà cung cấp (khai báo, sửa, ngừng) và bộ tương thích (khai báo kèm cấp
 * tài khoản DV001, cấp lại mật khẩu, mở khoá, ngừng). Mật khẩu vừa cấp hiện MỘT LẦN
 * trong `HopTaiKhoanVuaCap`. Tài khoản cho thiết bị kết nối trực tiếp cấp ở trang
 * chi tiết thiết bị, không ở đây.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viNhaCungCap } from '../ngon-ngu/vi-nha-cung-cap';
import {
  LoiApi,
  nhaCungCapApi,
  type BoTuongThich,
  type NhaCungCap,
  type NhaCungCapApi,
  type KetQuaThuToken,
  type TaiKhoanVuaCap,
  type XacThucGoiVao,
} from '../api/nha-cung-cap-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag } from '../thanh-phan/MauGiaoDien';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { HopTaiKhoanVuaCap } from '../thanh-phan/HopTaiKhoanVuaCap';
import { HopKiemThuTuanThu } from '../thanh-phan/HopKiemThuTuanThu';
import './nha-cung-cap.css';

const t = viNhaCungCap;

const thongDiepLoi = (e: unknown): string =>
  e instanceof LoiApi || e instanceof Error ? e.message : t.chung.thatBai;

/** "a, b ; c" → ['a','b','c'] — ô nhập danh sách phân tách bằng dấu phẩy/chấm phẩy/xuống dòng. */
const tachDs = (s: string): string[] =>
  s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter((x) => x !== '');

const dinhDangThoiDiem = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('vi-VN', { hour12: false }) : '—';

function TheTrangThai({ gt }: { gt: string }): ReactNode {
  return <TheTag kieu={gt === 'active' ? 'ok' : 'trung-tinh'}>{t.chung.trangThaiGiaTri[gt] ?? gt}</TheTag>;
}

/* ------------------------------ Biểu mẫu NCC ------------------------------ */

function BieuMauNhaCungCap({
  goc,
  api,
  onXong,
  onHuy,
}: {
  goc: NhaCungCap | null;
  api: NhaCungCapApi;
  onXong: (thongBao: string) => void;
  onHuy: () => void;
}): ReactNode {
  const [ma, datMa] = useState(goc?.maNhaCungCap ?? '');
  const [ten, datTen] = useState(goc?.ten ?? '');
  const [dauMoi, datDauMoi] = useState(goc?.dauMoiLienHe ?? '');
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const gui = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangGui(true);
    datLoi(null);
    try {
      if (goc) await api.sua(goc.id, { ten, dauMoiLienHe: dauMoi });
      else await api.tao({ maNhaCungCap: ma, ten, dauMoiLienHe: dauMoi || undefined });
      onXong(goc ? t.ncc.daSua : t.ncc.daTao);
    } catch (err) {
      datLoi(thongDiepLoi(err));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai ncc-bieu-mau"
        role="dialog"
        aria-modal="true"
        aria-label={goc ? t.ncc.sua : t.ncc.them}
        onSubmit={(e) => void gui(e)}
      >
        <h2>{goc ? t.ncc.sua : t.ncc.them}</h2>
        <label>
          {t.ncc.ma} <span className="ncc-bat-buoc">*</span>
          <input
            value={ma}
            onChange={(e) => datMa(e.target.value)}
            placeholder={t.ncc.maGoiY}
            required
            disabled={Boolean(goc)}
            maxLength={50}
            pattern="[A-Za-z0-9._\-]{2,50}"
          />
        </label>
        <label>
          {t.ncc.ten} <span className="ncc-bat-buoc">*</span>
          <input value={ten} onChange={(e) => datTen(e.target.value)} required maxLength={255} />
        </label>
        <label>
          {t.ncc.dauMoi}
          <input
            value={dauMoi}
            onChange={(e) => datDauMoi(e.target.value)}
            placeholder={t.ncc.dauMoiGoiY}
            maxLength={255}
          />
        </label>
        {loi ? (
          <p className="ncc-loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut" onClick={onHuy}>
            {t.chung.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {dangGui ? t.chung.dangGui : t.chung.luu}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------- Biểu mẫu bộ tương thích ------------------------ */

/**
 * Tài khoản nhà cung cấp cấp cho hệ thống nguồn để lấy token gọi vào adapter. Mật khẩu
 * chỉ ghi (không bao giờ hiện lại); "Thử lấy token" dùng tài khoản ĐÃ LƯU.
 */
function KhaiTaiKhoanGoiVao({
  goc,
  api,
  giaTri,
  onDoi,
}: {
  goc: BoTuongThich | null;
  api: NhaCungCapApi;
  giaTri: XacThucGoiVao & { matKhau: string };
  onDoi: (v: XacThucGoiVao & { matKhau: string }) => void;
}): ReactNode {
  const tt = t.btt.goiVao;
  const [ketQua, datKetQua] = useState<KetQuaThuToken | null>(null);
  const [dangThu, datDangThu] = useState(false);
  const doi = (k: keyof (XacThucGoiVao & { matKhau: string }), v: string): void =>
    onDoi({ ...giaTri, [k]: v });
  const thu = async (): Promise<void> => {
    if (!goc) return;
    datDangThu(true);
    datKetQua(null);
    try {
      datKetQua(await api.thuTokenBoTuongThich(goc.id));
    } catch (err) {
      datKetQua({ thanhCong: false, thoiGianMs: 0, loi: thongDiepLoi(err) });
    } finally {
      datDangThu(false);
    }
  };
  return (
    <fieldset className="ncc-dd" data-vung="tai-khoan-goi-vao">
      <legend>{tt.tieuDe}</legend>
      <p className="ncc-dd__mo-ta">{tt.moTa}</p>
      <label>
        {tt.urlToken}
        <input
          value={giaTri.urlToken ?? ''}
          placeholder={tt.urlTokenGoiY}
          spellCheck={false}
          onChange={(e) => doi('urlToken', e.target.value)}
        />
      </label>
      <div className="ncc-hai-cot">
        <label>
          {tt.taiKhoan}
          <input
            value={giaTri.taiKhoan ?? ''}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => doi('taiKhoan', e.target.value)}
          />
        </label>
        <label>
          {tt.matKhau}
          <input
            type="password"
            value={giaTri.matKhau}
            autoComplete="new-password"
            placeholder={goc?.coMatKhauGoiVao ? tt.matKhauDaLuu : ''}
            onChange={(e) => doi('matKhau', e.target.value)}
          />
        </label>
      </div>
      <details open={Boolean(giaTri.truongTaiKhoan || giaTri.truongMatKhau || giaTri.duongDanToken)}>
        <summary>{tt.nangCao}</summary>
        <p className="ncc-dd__mo-ta ncc-canh-bao">{tt.nangCaoCanhBao}</p>
        <div className="ncc-hai-cot">
          <label>
            {tt.truongTaiKhoan}
            <input
              value={giaTri.truongTaiKhoan ?? ''}
              placeholder="Username"
              onChange={(e) => doi('truongTaiKhoan', e.target.value)}
            />
          </label>
          <label>
            {tt.truongMatKhau}
            <input
              value={giaTri.truongMatKhau ?? ''}
              placeholder="Password"
              onChange={(e) => doi('truongMatKhau', e.target.value)}
            />
          </label>
        </div>
        <label>
          {tt.duongDanToken}
          <input
            value={giaTri.duongDanToken ?? ''}
            placeholder={tt.duongDanTokenGoiY}
            onChange={(e) => doi('duongDanToken', e.target.value)}
          />
        </label>
      </details>
      <div className="ncc-thu-token">
        <button
          type="button"
          className="nut nut--nho"
          data-hanh-dong="thu-token"
          disabled={!goc?.xacThucGoiVao?.taiKhoan || dangThu}
          title={goc?.xacThucGoiVao?.taiKhoan ? undefined : tt.canLuuTruoc}
          onClick={() => void thu()}
        >
          {dangThu ? tt.dangThu : tt.thu}
        </button>
        {!goc?.xacThucGoiVao?.taiKhoan ? <small>{tt.canLuuTruoc}</small> : null}
        {ketQua ? (
          <small className={ketQua.thanhCong ? 'ncc-thu-token__dat' : 'ncc-loi'} role="status">
            {ketQua.thanhCong
              ? dienVao(tt.thuDat, {
                  token: ketQua.tokenRutGon ?? '',
                  het: ketQua.hetHanLuc
                    ? new Date(ketQua.hetHanLuc).toLocaleString('vi-VN', { hour12: false })
                    : '—',
                  ms: ketQua.thoiGianMs,
                })
              : dienVao(tt.thuLoi, { loi: ketQua.loi ?? '' })}
          </small>
        ) : null}
      </div>
    </fieldset>
  );
}

/** Ghép URL gọi thật để xem trước: URL đầy đủ giữ nguyên, đường dẫn nối sau URL gốc. */
function ghepUrl(urlGoc: string, duongDan: string): string {
  if (/^https?:\/\//i.test(duongDan)) return duongDan;
  const d = duongDan.startsWith('/') ? duongDan : `/${duongDan}`;
  return `${urlGoc.replace(/\/+$/, '')}${d}`;
}

/**
 * Khai đường dẫn từng dịch vụ của adapter (DV002/DV003/DV004/DV009 và các thao tác phụ):
 * mỗi dòng một ô, gợi ý là đường dẫn mặc định, bên dưới xem trước URL hệ thống sẽ gọi.
 */
function KhaiDuongDanDichVu({
  urlGoc,
  giaTri,
  onDoi,
}: {
  urlGoc: string;
  giaTri: Record<string, string>;
  onDoi: (v: Record<string, string>) => void;
}): ReactNode {
  const tt = t.btt.duongDan;
  const dong = ([khoa, nhan, macDinh]: [string, string, string]): ReactNode => {
    const v = giaTri[khoa] ?? '';
    return (
      <label key={khoa} className="ncc-dd__dong">
        <span>{nhan}</span>
        <input
          value={v}
          placeholder={macDinh}
          spellCheck={false}
          onChange={(e) => onDoi({ ...giaTri, [khoa]: e.target.value })}
        />
        <small className="ncc-dd__xem">
          {tt.goiThu}: <code>{urlGoc ? ghepUrl(urlGoc, v.trim() || macDinh) : v.trim() || macDinh}</code>
        </small>
      </label>
    );
  };
  return (
    <fieldset className="ncc-dd" data-vung="duong-dan-dich-vu">
      <legend>{tt.tieuDe}</legend>
      <p className="ncc-dd__mo-ta">{tt.moTa}</p>
      {tt.mucDichVu.map(dong)}
      <details>
        <summary>{tt.khac}</summary>
        {tt.mucKhac.map(dong)}
      </details>
    </fieldset>
  );
}

function BieuMauBoTuongThich({
  goc,
  dsNcc,
  api,
  onTao,
  onSua,
  onHuy,
}: {
  goc: BoTuongThich | null;
  dsNcc: NhaCungCap[];
  api: NhaCungCapApi;
  onTao: (taiKhoan: TaiKhoanVuaCap) => void;
  onSua: (thongBao: string) => void;
  onHuy: () => void;
}): ReactNode {
  const [ma, datMa] = useState(goc?.maNhaCungCap ?? '');
  const [url, datUrl] = useState(goc?.urlGoc ?? '');
  const [noiDat, datNoiDat] = useState(goc?.noiDatAdapter ?? 'phia_nha_cung_cap');
  const [dichVu, datDichVu] = useState((goc?.dichVuHoTro ?? []).join(', '));
  const [dsIp, datDsIp] = useState((goc?.danhSachIpChoPhep ?? []).join(', '));
  const [tanSuat, datTanSuat] = useState(String(goc?.gioiHanTanSuat ?? 600));
  const [camKet, datCamKet] = useState(
    goc?.thoiGianPhanHoiCamKetMs ? String(goc.thoiGianPhanHoiCamKetMs) : '',
  );
  const [duongDan, datDuongDan] = useState<Record<string, string>>(goc?.duongDanDichVu ?? {});
  const [goiVao, datGoiVao] = useState<XacThucGoiVao & { matKhau: string }>({
    ...(goc?.xacThucGoiVao ?? {}),
    matKhau: '',
  });
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const gui = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangGui(true);
    datLoi(null);
    const tkGoiVao = (goiVao.taiKhoan ?? '').trim();
    const than = {
      // Tài khoản gọi vào adapter: trống mà trước đã có → xoá; mật khẩu trống = giữ mật khẩu đã lưu.
      xacThucGoiVao: tkGoiVao
        ? { ...goiVao, taiKhoan: tkGoiVao }
        : goc?.xacThucGoiVao?.taiKhoan
          ? ({ xoa: true } as const)
          : undefined,
      duongDanDichVu: Object.fromEntries(
        Object.entries(duongDan)
          .map(([k, v]) => [k, v.trim()])
          .filter(([, v]) => v !== ''),
      ),
      urlGoc: url,
      noiDatAdapter: noiDat,
      dichVuHoTro: tachDs(dichVu),
      danhSachIpChoPhep: tachDs(dsIp),
      gioiHanTanSuat: tanSuat === '' ? undefined : Number(tanSuat),
      thoiGianPhanHoiCamKetMs: camKet === '' ? null : Number(camKet),
    };
    try {
      if (goc) {
        await api.suaBoTuongThich(goc.id, than);
        onSua(t.btt.daSua);
      } else {
        const kq = await api.taoBoTuongThich({ ...than, maNhaCungCap: ma });
        onTao(kq.taiKhoan);
      }
    } catch (err) {
      datLoi(thongDiepLoi(err));
    } finally {
      datDangGui(false);
    }
  };

  const nccHoatDong = dsNcc.filter((n) => n.trangThai === 'active');

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai ncc-bieu-mau"
        role="dialog"
        aria-modal="true"
        aria-label={goc ? t.btt.sua : t.btt.them}
        onSubmit={(e) => void gui(e)}
      >
        <h2>{goc ? t.btt.sua : t.btt.them}</h2>
        <label>
          {t.btt.nhaCungCap} <span className="ncc-bat-buoc">*</span>
          <select value={ma} onChange={(e) => datMa(e.target.value)} required disabled={Boolean(goc)}>
            <option value="">{t.btt.chonNhaCungCap}</option>
            {(goc ? dsNcc : nccHoatDong).map((n) => (
              <option key={n.id} value={n.maNhaCungCap}>
                {n.maNhaCungCap} — {n.ten}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t.btt.urlGoc} <span className="ncc-bat-buoc">*</span>
          <input
            type="url"
            value={url}
            onChange={(e) => datUrl(e.target.value)}
            placeholder={t.btt.urlGocGoiY}
            required
            maxLength={300}
          />
        </label>
        <label>
          {t.btt.noiDat}
          <select value={noiDat} onChange={(e) => datNoiDat(e.target.value)}>
            {Object.entries(t.btt.noiDatGiaTri).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <KhaiTaiKhoanGoiVao goc={goc} api={api} giaTri={goiVao} onDoi={datGoiVao} />
        <KhaiDuongDanDichVu urlGoc={url} giaTri={duongDan} onDoi={datDuongDan} />
        <label>
          {t.btt.dichVu}
          <input value={dichVu} onChange={(e) => datDichVu(e.target.value)} placeholder={t.btt.dichVuGoiY} />
        </label>
        <label>
          {t.btt.dsIp}
          <input value={dsIp} onChange={(e) => datDsIp(e.target.value)} placeholder={t.btt.dsIpGoiY} />
        </label>
        <div className="ncc-hai-cot">
          <label>
            {t.btt.tanSuat}
            <input
              type="number"
              min={1}
              max={100000}
              value={tanSuat}
              onChange={(e) => datTanSuat(e.target.value)}
            />
          </label>
          <label>
            {t.btt.camKet}
            <input
              type="number"
              min={100}
              max={60000}
              value={camKet}
              onChange={(e) => datCamKet(e.target.value)}
            />
          </label>
        </div>
        {loi ? (
          <p className="ncc-loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut" onClick={onHuy}>
            {t.chung.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {dangGui ? t.chung.dangGui : t.chung.luu}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------------- Trang ---------------------------------- */

export function QuanTriNhaCungCap({ api = nhaCungCapApi }: { api?: NhaCungCapApi } = {}): ReactNode {
  const [moc, datMoc] = useState(0);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [suaNcc, datSuaNcc] = useState<NhaCungCap | null | 'moi'>(null);
  const [suaBtt, datSuaBtt] = useState<BoTuongThich | null | 'moi'>(null);
  const [hoiCapLai, datHoiCapLai] = useState<BoTuongThich | null>(null);
  const [hoiXoa, datHoiXoa] = useState<BoTuongThich | null>(null);
  const [kiemThu, datKiemThu] = useState<BoTuongThich | null>(null);
  const [taiKhoan, datTaiKhoan] = useState<TaiKhoanVuaCap | null>(null);

  const taiLai = (): void => datMoc((x) => x + 1);

  const ncc = dungTaiDuLieu<NhaCungCap[]>({
    tai: (signal) => api.danhSach(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [moc],
  });
  const btt = dungTaiDuLieu<BoTuongThich[]>({
    tai: (signal) => api.danhSachBoTuongThich(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [moc],
  });

  const lam = async (viec: () => Promise<unknown>, xong: string): Promise<void> => {
    datThongBao(null);
    try {
      await viec();
      datThongBao(xong);
      taiLai();
    } catch (err) {
      datThongBao(thongDiepLoi(err));
    }
  };

  const capLai = async (b: BoTuongThich): Promise<void> => {
    datHoiCapLai(null);
    datThongBao(null);
    try {
      datTaiKhoan(await api.capLaiBoTuongThich(b.id));
      taiLai();
    } catch (err) {
      datThongBao(thongDiepLoi(err));
    }
  };

  const cotNcc: CotBang<NhaCungCap>[] = [
    { khoa: 'maNhaCungCap', nhan: t.ncc.ma, ve: (d) => <strong>{d.maNhaCungCap}</strong> },
    { khoa: 'ten', nhan: t.ncc.ten, ve: (d) => d.ten },
    { khoa: 'dauMoiLienHe', nhan: t.ncc.dauMoi, ve: (d) => d.dauMoiLienHe ?? '—' },
    { khoa: 'soBoTuongThich', nhan: t.ncc.soBtt, canPhai: true, ve: (d) => d.soBoTuongThich },
    { khoa: 'soThietBi', nhan: t.ncc.soThietBi, canPhai: true, ve: (d) => d.soThietBi },
    { khoa: 'trangThai', nhan: t.chung.trangThai, ve: (d) => <TheTrangThai gt={d.trangThai} /> },
    {
      khoa: 'hanhDong',
      nhan: t.chung.thaoTac,
      ve: (d) => (
        <span className="bang__hanh-dong">
          <button type="button" className="nut nut--nho" onClick={() => datSuaNcc(d)}>
            {t.ncc.sua}
          </button>
          <button
            type="button"
            className="nut nut--nho"
            onClick={() =>
              void lam(
                () => api.sua(d.id, { trangThai: d.trangThai === 'active' ? 'inactive' : 'active' }),
                t.ncc.daSua,
              )
            }
          >
            {d.trangThai === 'active' ? t.chung.ngung : t.chung.kichHoat}
          </button>
        </span>
      ),
    },
  ];

  const cotBtt: CotBang<BoTuongThich>[] = [
    {
      khoa: 'maNhaCungCap',
      nhan: t.btt.nhaCungCap,
      ve: (d) => (
        <>
          <strong>{d.maNhaCungCap}</strong>
          <span className="bang__dong-phu">{d.tenNhaCungCap}</span>
        </>
      ),
    },
    {
      khoa: 'urlGoc',
      nhan: t.btt.urlGoc,
      ve: (d) => (
        <>
          <code className="ncc-ma">{d.urlGoc}</code>
          <span className="bang__dong-phu">{t.btt.noiDatGiaTri[d.noiDatAdapter] ?? d.noiDatAdapter}</span>
          {d.xacThucGoiVao?.taiKhoan ? (
            <span className="bang__dong-phu">
              {dienVao(t.btt.goiVao.coTaiKhoan, { tk: d.xacThucGoiVao.taiKhoan })}
            </span>
          ) : null}
          {Object.keys(d.duongDanDichVu ?? {}).length > 0 ? (
            <span
              className="bang__dong-phu"
              title={Object.entries(d.duongDanDichVu)
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n')}
            >
              {dienVao(t.btt.duongDan.soKhaiRieng, { so: Object.keys(d.duongDanDichVu).length })}
            </span>
          ) : null}
        </>
      ),
    },
    {
      khoa: 'serviceDvNcc',
      nhan: t.btt.serviceDvNcc,
      ve: (d) =>
        d.serviceDvNcc ? (
          <>
            <code className="ncc-ma">{d.serviceDvNcc.tenService}</code>
            <span className="bang__dong-phu">
              {dienVao(t.btt.serviceCong, { cong: d.serviceDvNcc.congNoiBo })}{' '}
              <TheTag
                kieu={
                  d.serviceDvNcc.trangThai !== 'active'
                    ? 'trung-tinh'
                    : d.serviceDvNcc.trangThaiVanHanh === 'dang_chay'
                      ? 'ok'
                      : d.serviceDvNcc.trangThaiVanHanh === 'loi' ||
                          d.serviceDvNcc.trangThaiVanHanh === 'cau_dao_mo'
                        ? 'do'
                        : 'vang'
                }
              >
                {d.serviceDvNcc.trangThai !== 'active'
                  ? t.btt.serviceThuHoi
                  : (t.btt.vanHanhGiaTri[d.serviceDvNcc.trangThaiVanHanh] ?? d.serviceDvNcc.trangThaiVanHanh)}
              </TheTag>
            </span>
          </>
        ) : (
          <span className="bang__dong-phu">{t.btt.serviceChuaCo}</span>
        ),
    },
    { khoa: 'dichVuHoTro', nhan: t.btt.dichVu, ve: (d) => d.dichVuHoTro.join(', ') || '—' },
    { khoa: 'danhSachIpChoPhep', nhan: t.btt.dsIp, ve: (d) => d.danhSachIpChoPhep.join(', ') || '—' },
    {
      khoa: 'tenDangNhap',
      nhan: t.btt.tenDangNhap,
      ve: (d) => (
        <>
          <code className="ncc-ma">{d.tenDangNhap ?? '—'}</code>
          {d.biKhoaDen ? (
            <span className="bang__dong-phu">
              <TheTag kieu="do">
                {t.btt.biKhoa} {dinhDangThoiDiem(d.biKhoaDen)}
              </TheTag>
            </span>
          ) : null}
        </>
      ),
    },
    {
      khoa: 'trangThaiKiemThuTuanThu',
      nhan: t.btt.kiemThu,
      ve: (d) => (
        <TheTag
          kieu={
            d.trangThaiKiemThuTuanThu === 'dat'
              ? 'ok'
              : d.trangThaiKiemThuTuanThu === 'khong_dat'
                ? 'do'
                : 'vang'
          }
        >
          {t.btt.kiemThuGiaTri[d.trangThaiKiemThuTuanThu] ?? d.trangThaiKiemThuTuanThu}
        </TheTag>
      ),
    },
    { khoa: 'trangThai', nhan: t.chung.trangThai, ve: (d) => <TheTrangThai gt={d.trangThai} /> },
    {
      khoa: 'hanhDong',
      nhan: t.chung.thaoTac,
      ve: (d) => (
        <span className="bang__hanh-dong">
          <button type="button" className="nut nut--nho" onClick={() => datSuaBtt(d)}>
            {t.btt.sua}
          </button>
          <button type="button" className="nut nut--nho" onClick={() => datHoiCapLai(d)}>
            {t.btt.capLai}
          </button>
          <button
            type="button"
            className="nut nut--nho"
            data-hanh-dong="kiem-thu-tuan-thu"
            onClick={() => datKiemThu(d)}
          >
            {t.btt.chayKiemThu}
          </button>
          {d.biKhoaDen ? (
            <button
              type="button"
              className="nut nut--nho"
              onClick={() => void lam(() => api.moKhoaBoTuongThich(d.id), t.btt.daMoKhoa)}
            >
              {t.btt.moKhoa}
            </button>
          ) : null}
          <button
            type="button"
            className="nut nut--nho"
            onClick={() =>
              void lam(
                () =>
                  api.suaBoTuongThich(d.id, { trangThai: d.trangThai === 'active' ? 'inactive' : 'active' }),
                t.btt.daSua,
              )
            }
          >
            {d.trangThai === 'active' ? t.chung.ngung : t.chung.kichHoat}
          </button>
          <button
            type="button"
            className="nut nut--nho nut--nguy-hiem"
            data-hanh-dong="xoa-bo-tuong-thich"
            disabled={d.trangThai === 'active'}
            title={d.trangThai === 'active' ? t.btt.xoaChiKhiNgung : undefined}
            onClick={() => datHoiXoa(d)}
          >
            {t.btt.xoa}
          </button>
        </span>
      ),
    },
  ];

  const dsNcc = ncc.duLieu ?? [];
  const coNccHoatDong = dsNcc.some((n) => n.trangThai === 'active');

  return (
    <section className="man-hinh ncc-trang" data-man-hinh="quan-tri-nha-cung-cap">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>
      <p className="ncc-mo-ta">{t.moTa}</p>

      <aside className="ncc-huong-dan" aria-label={t.huongDan.tieuDe}>
        <div>
          <h2>1. {t.huongDan.gianTiep}</h2>
          <p>{t.huongDan.gianTiepMoTa}</p>
        </div>
        <div>
          <h2>2. {t.huongDan.trucTiep}</h2>
          <p>{t.huongDan.trucTiepMoTa}</p>
        </div>
        <p className="ncc-huong-dan__tai-lieu">{t.huongDan.taiLieu}</p>
      </aside>

      {thongBao ? (
        <p className="ncc-thong-bao" role="status">
          {thongBao}
        </p>
      ) : null}

      <div className="ncc-dau-bang">
        <h2>{t.ncc.tieuDe}</h2>
        <button type="button" className="nut nut--chinh" onClick={() => datSuaNcc('moi')}>
          {t.ncc.them}
        </button>
      </div>
      <BangDuLieu<NhaCungCap>
        nhan={t.ncc.tieuDe}
        cot={cotNcc}
        dong={dsNcc}
        khoaDong={(d) => d.id}
        trangThai={ncc.trangThai}
        loi={ncc.loi}
        taiLau={ncc.taiLau}
        onThuLai={taiLai}
        tong={dsNcc.length}
        trang={1}
        soDongMoiTrang={Math.max(SO_DONG_MAC_DINH, dsNcc.length)}
        onDoiTrang={() => undefined}
        onDoiSoDong={() => undefined}
        dangLoc={false}
        onXoaLoc={() => undefined}
        moTaRong={t.ncc.rong}
        nhanHanhDongRong={t.ncc.them}
        onHanhDongRong={() => datSuaNcc('moi')}
      />

      <div className="ncc-dau-bang">
        <h2>{t.btt.tieuDe}</h2>
        <button
          type="button"
          className="nut nut--chinh"
          disabled={!coNccHoatDong}
          title={coNccHoatDong ? undefined : t.btt.canNccTruoc}
          onClick={() => datSuaBtt('moi')}
        >
          {t.btt.them}
        </button>
      </div>
      <BangDuLieu<BoTuongThich>
        nhan={t.btt.tieuDe}
        cot={cotBtt}
        dong={btt.duLieu ?? []}
        khoaDong={(d) => d.id}
        trangThai={btt.trangThai}
        loi={btt.loi}
        taiLau={btt.taiLau}
        onThuLai={taiLai}
        tong={btt.duLieu?.length ?? 0}
        trang={1}
        soDongMoiTrang={Math.max(SO_DONG_MAC_DINH, btt.duLieu?.length ?? 0)}
        onDoiTrang={() => undefined}
        onDoiSoDong={() => undefined}
        dangLoc={false}
        onXoaLoc={() => undefined}
        moTaRong={coNccHoatDong ? t.btt.rong : t.btt.canNccTruoc}
      />

      {suaNcc ? (
        <BieuMauNhaCungCap
          goc={suaNcc === 'moi' ? null : suaNcc}
          api={api}
          onHuy={() => datSuaNcc(null)}
          onXong={(tb) => {
            datSuaNcc(null);
            datThongBao(tb);
            taiLai();
          }}
        />
      ) : null}

      {suaBtt ? (
        <BieuMauBoTuongThich
          goc={suaBtt === 'moi' ? null : suaBtt}
          dsNcc={dsNcc}
          api={api}
          onHuy={() => datSuaBtt(null)}
          onTao={(tk) => {
            datSuaBtt(null);
            datTaiKhoan(tk);
            taiLai();
          }}
          onSua={(tb) => {
            datSuaBtt(null);
            datThongBao(tb);
            taiLai();
          }}
        />
      ) : null}

      {hoiCapLai ? (
        <HopXacNhan
          tieuDe={t.btt.xacNhanCapLaiTieuDe}
          heQua={dienVao(t.btt.xacNhanCapLai, { ten: hoiCapLai.tenDangNhap ?? hoiCapLai.maNhaCungCap })}
          nhanThucHien={t.btt.capLai}
          onHuy={() => datHoiCapLai(null)}
          onThucHien={() => void capLai(hoiCapLai)}
        />
      ) : null}

      {kiemThu ? (
        <HopKiemThuTuanThu
          btt={kiemThu}
          chay={api.kiemThuTuanThu}
          onDong={(daChay) => {
            datKiemThu(null);
            if (daChay) taiLai();
          }}
        />
      ) : null}

      {hoiXoa ? (
        <HopXacNhan
          tieuDe={t.btt.xacNhanXoaTieuDe}
          heQua={dienVao(t.btt.xacNhanXoa, {
            ten: hoiXoa.tenDangNhap ?? hoiXoa.id,
            ncc: hoiXoa.tenNhaCungCap ?? hoiXoa.maNhaCungCap,
          })}
          nhanThucHien={t.btt.xoa}
          onHuy={() => datHoiXoa(null)}
          onThucHien={() => {
            const b = hoiXoa;
            datHoiXoa(null);
            void (async () => {
              datThongBao(null);
              try {
                const kq = await api.xoaBoTuongThich(b.id);
                datThongBao(kq.serviceDvNcc === 'go' ? t.btt.daXoaGoService : t.btt.daXoa);
                taiLai();
              } catch (err) {
                datThongBao(thongDiepLoi(err));
              }
            })();
          }}
        />
      ) : null}

      {taiKhoan ? <HopTaiKhoanVuaCap taiKhoan={taiKhoan} onDong={() => datTaiKhoan(null)} /> : null}
    </section>
  );
}
